import { useState, useEffect, useRef } from 'react';
import { useFlyerGenerator, type FlyerData } from '@/hooks/useFlyerGenerator';
import { formatCurrency } from '@/lib/format';

interface FlyerImovelModalProps {
  imovel: FlyerData;
  isOpen: boolean;
  onClose: () => void;
}

export default function FlyerImovelModal({ imovel, isOpen, onClose }: FlyerImovelModalProps) {
  const { generateFlyer } = useFlyerGenerator();
  const [flyerUrl, setFlyerUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [baixado, setBaixado] = useState(false);
  const [erro, setErro] = useState('');
  const geradoRef = useRef(false);

  useEffect(() => {
    if (isOpen && !geradoRef.current) {
      geradoRef.current = true;
      handleGerar();
    }
    if (!isOpen) {
      geradoRef.current = false;
      setFlyerUrl('');
      setErro('');
      setBaixado(false);
    }
  }, [isOpen]);

  async function handleGerar() {
    setLoading(true);
    setErro('');
    try {
      const url = await generateFlyer(imovel);
      setFlyerUrl(url);
    } catch (e: any) {
      setErro('Não foi possível gerar o flyer. Tente novamente.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  function handleBaixar() {
    if (!flyerUrl) return;
    const a = document.createElement('a');
    a.href = flyerUrl;
    a.download = `flyer-${imovel.codigo}-${imovel.slug}.png`;
    a.click();
    setBaixado(true);
    setTimeout(() => setBaixado(false), 3000);
  }

  async function handleCompartilharNativo() {
    if (!flyerUrl || !navigator.share) return;
    try {
      const blob = await (await fetch(flyerUrl)).blob();
      const file = new File([blob], `flyer-${imovel.codigo}.png`, { type: 'image/png' });
      await navigator.share({
        title: imovel.titulo,
        text: `${imovel.tipo} em ${imovel.bairro} — ${formatCurrency(imovel.preco)}`,
        files: [file],
      });
    } catch {
      // Fallback: só baixa
      handleBaixar();
    }
  }

  const hasNativeShare = typeof navigator !== 'undefined' && !!navigator.canShare;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[110] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[95vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-600 to-orange-700 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-lg">
              <i className="ri-image-2-line text-white text-xl"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Flyer Digital</h2>
              <p className="text-orange-100 text-xs">{imovel.codigo} · {imovel.titulo}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Conteúdo */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">

          {/* Preview do flyer */}
          <div className="relative bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center"
            style={{ minHeight: 320 }}>
            {loading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 z-10">
                <div className="w-12 h-12 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-sm text-gray-500 font-medium">Gerando flyer...</p>
                <p className="text-xs text-gray-400 mt-1">Carregando foto e montando layout</p>
              </div>
            )}

            {erro && !loading && (
              <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <i className="ri-error-warning-line text-4xl text-red-400 mb-3"></i>
                <p className="text-red-600 font-semibold mb-1">Erro ao gerar flyer</p>
                <p className="text-sm text-gray-500 mb-4">{erro}</p>
                <button
                  onClick={handleGerar}
                  className="px-5 py-2 bg-orange-600 text-white rounded-lg text-sm font-semibold hover:bg-orange-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Tentar novamente
                </button>
              </div>
            )}

            {flyerUrl && !loading && (
              <img
                src={flyerUrl}
                alt="Flyer do imóvel"
                className="w-full rounded-xl"
                style={{ maxHeight: 520, objectFit: 'contain' }}
              />
            )}
          </div>

          {/* Info */}
          {flyerUrl && !loading && (
            <div className="bg-orange-50 border border-orange-100 rounded-xl px-4 py-3 flex items-start gap-3">
              <i className="ri-information-line text-orange-500 text-lg mt-0.5 flex-shrink-0"></i>
              <p className="text-sm text-orange-800 leading-relaxed">
                O flyer foi gerado em <strong>1080×1350px</strong> (formato ideal para WhatsApp e Instagram).
                Baixe a imagem e envie diretamente na conversa do WhatsApp para que apareça em tamanho completo.
              </p>
            </div>
          )}

          {/* Botões de ação */}
          {flyerUrl && !loading && (
            <div className="space-y-3">
              {/* Baixar */}
              <button
                onClick={handleBaixar}
                className={`w-full flex items-center justify-center gap-3 font-bold py-3.5 rounded-xl transition-all whitespace-nowrap cursor-pointer ${
                  baixado
                    ? 'bg-green-500 text-white'
                    : 'bg-orange-600 hover:bg-orange-700 text-white'
                }`}
              >
                <i className={`${baixado ? 'ri-check-line' : 'ri-download-2-line'} text-xl`}></i>
                {baixado ? 'Imagem salva!' : 'Baixar Flyer (PNG)'}
              </button>

              {/* Compartilhar nativo (mobile) */}
              {hasNativeShare && (
                <button
                  onClick={handleCompartilharNativo}
                  className="w-full flex items-center justify-center gap-3 bg-green-500 hover:bg-green-600 text-white font-bold py-3.5 rounded-xl transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                  Compartilhar imagem direto
                </button>
              )}

              {/* Regenerar */}
              <button
                onClick={() => { geradoRef.current = false; handleGerar(); }}
                className="w-full flex items-center justify-center gap-2 text-gray-500 hover:text-gray-700 py-2 text-sm transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                Regenerar flyer
              </button>
            </div>
          )}

          {/* Instruções de uso */}
          {flyerUrl && !loading && (
            <div className="border border-gray-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Como usar</p>
              <div className="space-y-2">
                {[
                  { n: '1', text: 'Clique em "Baixar Flyer" para salvar a imagem no seu dispositivo' },
                  { n: '2', text: 'Abra o WhatsApp e selecione a conversa do cliente' },
                  { n: '3', text: 'Anexe a imagem salva e envie — aparece em tamanho completo!' },
                  { n: '4', text: 'No celular, use "Compartilhar imagem direto" para enviar sem baixar' },
                ].map((step) => (
                  <div key={step.n} className="flex items-start gap-3">
                    <span className="w-6 h-6 flex-shrink-0 flex items-center justify-center bg-orange-100 text-orange-600 rounded-full text-xs font-bold">
                      {step.n}
                    </span>
                    <p className="text-sm text-gray-600 leading-relaxed">{step.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
